import { useState, type FormEvent } from 'react'
import { X } from 'lucide-react'
import { ApiError } from '@/api/client'
import { DEAL_CONTACT_ROLES } from '@/api/deals'
import { useAgencyQuery, useUpdateAgency, useUploadAgencyLogo } from '@/features/agency/useAgency'
import { useAuthStore } from '@/features/auth/authStore'
import { useUsers } from '@/features/team/useTeam'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import Select from '@/components/ui/Select'

const MAX_ROLES = 20
const ROLE_PATTERN = /^[a-z_]{1,32}$/

/** Supported display currencies — mirrors the backend whitelist. */
const CURRENCIES = ['AUD', 'USD', 'NZD', 'GBP', 'EUR', 'CAD', 'SGD']

/** Zones offered for the agency's local time (sequence sending hours are interpreted in it). */
const TIMEZONES = [
  'Australia/Brisbane', 'Australia/Sydney', 'Australia/Melbourne', 'Australia/Adelaide',
  'Australia/Perth', 'Pacific/Auckland', 'Asia/Singapore', 'Europe/London', 'America/New_York',
  'America/Los_Angeles', 'Asia/Almaty', 'UTC',
]

const PRESETS = [
  { label: 'ProfitPipe', primary: '#3262B5', dark: '#0B104F' },
  { label: 'Calibre', primary: '#4E6772', dark: '#3B4E57' },
  { label: 'Forest', primary: '#16a34a', dark: '#14532d' },
  { label: 'Plum', primary: '#7c3aed', dark: '#4c1d95' },
]

export default function BrandingTab() {
  const agency = useAuthStore((s) => s.agency)
  const update = useUpdateAgency()
  const uploadLogo = useUploadAgencyLogo()

  // The persisted auth-store agency can predate the dealContactRoles field, so the role
  // list is initialised from a fresh server fetch — otherwise saving branding from a
  // stale session would silently replace custom roles with the built-in defaults.
  const agencyQuery = useAgencyQuery()

  const [name, setName] = useState(agency?.name ?? '')
  const [brandColor, setBrandColor] = useState(agency?.brandColor ?? '#3262B5')
  const [brandColorDark, setBrandColorDark] = useState(agency?.brandColorDark ?? '#0B104F')
  const [logoUrl, setLogoUrl] = useState(agency?.logoUrl ?? '')
  // null = the user hasn't touched the chips; the list shown then mirrors the server's.
  const [rolesDraft, setRolesDraft] = useState<string[] | null>(null)
  const serverRoles = agencyQuery.data
    ? agencyQuery.data.dealContactRoles.length
      ? agencyQuery.data.dealContactRoles
      : DEAL_CONTACT_ROLES
    : null
  const roles = rolesDraft ?? serverRoles
  // null = the user hasn't touched the select; the value shown then mirrors the server's.
  const [currencyDraft, setCurrencyDraft] = useState<string | null>(null)
  const serverCurrency = agencyQuery.data?.currency ?? null
  const currency = currencyDraft ?? serverCurrency ?? 'AUD'
  const [timezoneDraft, setTimezoneDraft] = useState<string | null>(null)
  const timezone = timezoneDraft ?? agencyQuery.data?.timezone ?? 'UTC'
  // '' = no contact chosen: escalations go to the first active admin.
  const [escalationDraft, setEscalationDraft] = useState<string | null>(null)
  const { data: users } = useUsers()
  const activeAdmins = (users ?? []).filter((u) => u.role === 'AGENCY_ADMIN' && u.status === 'active')
  const savedEscalation = agencyQuery.data?.escalationUserId ?? ''
  // A contact who is no longer an active admin is skipped by the engine — show the fallback it uses.
  const escalationUserId =
    escalationDraft ?? (activeAdmins.some((u) => u.id === savedEscalation) ? savedEscalation : '')
  const [newRole, setNewRole] = useState('')
  const [roleError, setRoleError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [rolesInvalid, setRolesInvalid] = useState(false)
  // Lead source dropdown options — same edit pattern as roles, but an empty list is allowed.
  const [leadSourceDraft, setLeadSourceDraft] = useState<string[] | null>(null)
  const [newLeadSource, setNewLeadSource] = useState('')
  const serverLeadSources = agencyQuery.data?.leadSourceOptions ?? null
  const leadSources = leadSourceDraft ?? serverLeadSources

  function addRole() {
    if (!roles) return
    const r = newRole.trim().toLowerCase()
    if (!r) return
    if (!ROLE_PATTERN.test(r)) {
      setRoleError('Roles are 1–32 lowercase letters or underscores.')
      return
    }
    if (roles.includes(r)) {
      setRoleError('That role is already on the list.')
      return
    }
    if (roles.length >= MAX_ROLES) {
      setRoleError(`At most ${MAX_ROLES} roles.`)
      return
    }
    setRoleError(null)
    setRolesDraft([...roles, r])
    setNewRole('')
  }

  function removeRole(r: string) {
    if (!roles || roles.length <= 1) return // keep at least one role
    setRoleError(null)
    setRolesDraft(roles.filter((x) => x !== r))
  }

  function addLeadSource() {
    const v = newLeadSource.trim()
    if (!v) return
    const list = leadSources ?? []
    if (list.some((x) => x.toLowerCase() === v.toLowerCase())) return
    setLeadSourceDraft([...list, v])
    setNewLeadSource('')
  }

  function removeLeadSource(v: string) {
    setLeadSourceDraft((leadSources ?? []).filter((x) => x !== v))
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    setRolesInvalid(false)
    // Saving updates the auth store, which re-applies the brand live via the App-level effect.
    // The backend replaces the whole record, so pass brandColor50 through untouched.
    update.mutate(
      {
        name,
        brandColor,
        brandColorDark,
        brandColor50: agency?.brandColor50 ?? null,
        logoUrl: logoUrl.trim() || null,
        // Only send the currency the user actually changed; omitted = backend keeps the current one.
        currency: currencyDraft ?? undefined,
        // Same rule for the timezone: omitted = backend keeps the current one.
        timezone: timezoneDraft ?? undefined,
        escalationUserId: escalationDraft ?? undefined,
        // Only send roles the user actually edited; omitted = backend keeps the current list.
        dealContactRoles: rolesDraft ?? undefined,
        // Same rule for the lead-source options.
        leadSourceOptions: leadSourceDraft ?? undefined,
      },
      {
        onSuccess: () => {
          setRolesDraft(null) // back to mirroring the (now updated) server list
          setCurrencyDraft(null)
          setTimezoneDraft(null)
          setEscalationDraft(null)
          setLeadSourceDraft(null)
        },
        onError: (err) => {
          setFormError(`${apiErrorMessage(err)} Nothing was saved.`)
          if (err instanceof ApiError && err.code === 'agency.roles_invalid') setRolesInvalid(true)
        },
      },
    )
  }

  return (
    <div className="max-w-3xl">

      <form onSubmit={onSubmit} className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Workspace name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Workspace ID (slug)</label>
          <input
            value={agency?.slug ?? ''}
            readOnly
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
          />
          <p className="text-xs text-gray-400 mt-1">
            Your workspace identifier — used when signing in if your email belongs to more than one workspace.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Logo</label>
          <div className="flex items-center gap-3">
            <input
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://…/logo.png"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
            />
            <label className="text-sm px-3 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 whitespace-nowrap">
              {uploadLogo.isPending ? 'Uploading…' : 'Upload'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                className="hidden"
                disabled={uploadLogo.isPending}
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) uploadLogo.mutate(file, { onSuccess: (a) => setLogoUrl(a.logoUrl ?? '') })
                  e.target.value = '' // allow re-selecting the same file
                }}
              />
            </label>
            {logoUrl.trim() && (
              <img
                src={logoUrl.trim()}
                alt="Logo preview"
                className="h-9 max-w-24 object-contain rounded border border-gray-100 bg-gray-50 px-1"
              />
            )}
          </div>
          {uploadLogo.error && <p className="text-danger text-xs mt-1">{apiErrorMessage(uploadLogo.error)}</p>}
          <p className="text-[11px] text-gray-400 mt-1">Paste a URL or upload a file (PNG/JPEG/SVG/WEBP/GIF, under 512 KB). Shown in the sidebar; leave empty for the default mark.</p>
        </div>

        <div className="flex gap-6">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Primary colour</label>
            <input
              type="color"
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              className="h-10 w-16 rounded border border-gray-200 bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Sidebar / dark</label>
            <input
              type="color"
              value={brandColorDark}
              onChange={(e) => setBrandColorDark(e.target.value)}
              className="h-10 w-16 rounded border border-gray-200 bg-white"
            />
          </div>
        </div>

        <div>
          <span className="block text-xs font-medium text-gray-500 mb-2">Presets</span>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => {
                  setBrandColor(p.primary)
                  setBrandColorDark(p.dark)
                }}
                className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg text-xs hover:border-brand-primary/40"
              >
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.primary }} />
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Currency</label>
          <Select
            size="md"
            value={currency}
            onChange={(e) => setCurrencyDraft(e.target.value)}
            className="w-40"
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <p className="text-[11px] text-gray-400 mt-1">
            Deal values are shown with this currency’s sign and separators.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Timezone</label>
          <Select
            size="md"
            value={timezone}
            onChange={(e) => setTimezoneDraft(e.target.value)}
            className="w-56"
          >
            {(TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES]).map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </Select>
          <p className="text-[11px] text-gray-400 mt-1">
            Your agency’s local time — what “today” means for follow-ups, Focus and the dashboard, and the
            hours sequences send in.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Escalations go to</label>
          <Select
            size="md"
            value={escalationUserId}
            onChange={(e) => setEscalationDraft(e.target.value)}
            className="w-56"
          >
            <option value="">First admin{activeAdmins[0] ? ` (${activeAdmins[0].fullName})` : ''}</option>
            {activeAdmins.map((u) => (
              <option key={u.id} value={u.id}>
                {u.fullName}
              </option>
            ))}
          </Select>
          <p className="text-[11px] text-gray-400 mt-1">
            When a deal sits in limbo for two weeks, one task goes to the owner’s team lead — or to this admin
            if they have none. A week later, “No action taken” comes to this admin.
          </p>
        </div>

        <div className="pt-1 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-500 mt-3 mb-1">Deal contact roles</label>
          <p className="text-[11px] text-gray-400 mb-2">
            The roles a contact can play on a deal. Removing a role keeps it on existing deals.
          </p>
          {roles ? (
            <>
              <div
                className={`flex flex-wrap gap-1.5 mb-2 ${
                  rolesInvalid ? 'ring-1 ring-danger/60 rounded-lg p-1.5' : ''
                }`}
              >
                {roles.map((r) => (
                  <span
                    key={r}
                    className="inline-flex items-center gap-1 text-[11px] uppercase bg-brand-primary/15 text-brand-primary rounded px-1.5 py-0.5"
                  >
                    {r}
                    <button
                      type="button"
                      onClick={() => removeRole(r)}
                      disabled={roles.length <= 1}
                      className="text-brand-primary/60 hover:text-danger disabled:opacity-40"
                      title={roles.length <= 1 ? 'At least one role is required' : `Remove '${r}'`}
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addRole()
                    }
                  }}
                  placeholder="e.g. referrer"
                  className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                />
                <button
                  type="button"
                  onClick={addRole}
                  disabled={!newRole.trim() || roles.length >= MAX_ROLES}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-brand-dark hover:border-brand-primary/40 disabled:opacity-50"
                >
                  Add role
                </button>
              </div>
              {rolesInvalid && (
                <p className="text-danger text-xs mt-1">The server rejected this role list — fix it and save again.</p>
              )}
              {roleError && <p className="text-danger text-xs mt-1">{roleError}</p>}
            </>
          ) : agencyQuery.isError ? (
            <p className="text-danger text-xs">
              Couldn’t load the current roles — {apiErrorMessage(agencyQuery.error)}{' '}
              <button type="button" onClick={() => void agencyQuery.refetch()} className="font-medium underline">
                retry
              </button>
            </p>
          ) : (
            <p className="text-xs text-gray-400">Loading roles…</p>
          )}
        </div>

        <div className="pt-1 border-t border-gray-100">
          <label className="block text-xs font-medium text-gray-500 mt-3 mb-1">Lead source options</label>
          <p className="text-[11px] text-gray-400 mb-2">
            Choices offered in the contact “Lead source” dropdown. Removing one keeps it on existing contacts.
          </p>
          {leadSources ? (
            <>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {leadSources.length === 0 && (
                  <span className="text-[11px] text-gray-400">No options yet — add some below.</span>
                )}
                {leadSources.map((s) => (
                  <span
                    key={s}
                    className="inline-flex items-center gap-1 text-[11px] bg-brand-primary/15 text-brand-primary rounded px-1.5 py-0.5"
                  >
                    {s}
                    <button
                      type="button"
                      onClick={() => removeLeadSource(s)}
                      className="text-brand-primary/60 hover:text-danger"
                      title={`Remove '${s}'`}
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  value={newLeadSource}
                  onChange={(e) => setNewLeadSource(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addLeadSource()
                    }
                  }}
                  placeholder="e.g. Website"
                  className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                />
                <button
                  type="button"
                  onClick={addLeadSource}
                  disabled={!newLeadSource.trim()}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-brand-dark hover:border-brand-primary/40 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-400">Loading…</p>
          )}
        </div>

        {formError && (
          <div className="flex items-center justify-between bg-danger/10 border border-danger/30 text-danger text-xs rounded-lg px-3 py-2">
            <span>{formError}</span>
            <button type="button" onClick={() => setFormError(null)} className="ml-3 font-medium shrink-0">
              dismiss
            </button>
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={update.isPending}
            className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60"
          >
            {update.isPending ? 'Saving…' : 'Save & apply'}
          </button>
          {update.isSuccess && <span className="text-xs text-success">Applied ✓</span>}
        </div>
      </form>
    </div>
  )
}
